import type {ComponentPropsWithRef, JSX} from 'react'
import createStyledComponent from '../models/StyledComponent'
import {BaseObject, KnownTarget, WebTarget} from '../types'
import domElements, {SupportedHTMLElements} from '../utils/domElements'
import constructWithOptions, {Styled as StyledInstance} from './constructWithOptions'

const baseStyled = <Target extends WebTarget, InjectedProps extends object = BaseObject>(
  tag: Target,
) =>
  constructWithOptions<
    'web',
    Target,
    Target extends KnownTarget ? ComponentPropsWithRef<Target> & InjectedProps : InjectedProps
  >(createStyledComponent, tag)

const styled = baseStyled as typeof baseStyled & {
  [E in SupportedHTMLElements]: StyledInstance<'web', E, JSX.IntrinsicElements[E]>
}

// Shorthands for all valid HTML Elements
domElements.forEach((domElement) => {
  styled[domElement] = baseStyled<typeof domElement>(domElement) as StyledInstance<
    'web',
    typeof domElement,
    JSX.IntrinsicElements[typeof domElement]
  >
})

export default styled
export {type StyledInstance}

/**
 * This is the type of the `styled` HOC.
 */
export type Styled = typeof styled

/**
 * Use this higher-order type for scenarios where you are wrapping `styled`
 * and providing extra props as a third-party library.
 */
export type LibraryStyled<LibraryProps extends object = BaseObject> = <Target extends WebTarget>(
  tag: Target,
) => typeof baseStyled<Target, LibraryProps>
